import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "components/UI";
import classnames from "classnames";
import { AuthContext } from "utils/context";
import { useDispatch } from "react-redux";
import Actions from "actions";
import { pngLogoWhite } from "assets";
import StagingBanner from "../../../components/UI/StagingBanner";
import { isProduction } from "../../../utils/config";
import { PlatformMaintenanceBanner } from "components/UI";
import useCheckSignupPause from "hooks/useCheckSignupPause";
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { fontSize } from "tailwindcss/defaultTheme";

const MD_SCREEN = 768;

const Navbar = () => {
  const [menuIsOpen, setMenuOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [dimensions, setDimensions] = useState({
    height: window.innerHeight,
    width: window.innerWidth,
  });
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { isSubscribed } = useContext(AuthContext);
  const dispatch = useDispatch();
  const isTablet = useMediaQuery('(min-width: 1020px) and (max-width: 1279px)');

  useEffect(() => {
    const handleScroll = () => setScrollPosition(window.pageYOffset);
    const handleResize = () =>
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
      });
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (dimensions.width >= MD_SCREEN) setMenuOpen(false);
  }, [dimensions]);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const Logo = () => (
    <div className="flex-shrink-0">
      <Link to="/">
        <img src={pngLogoWhite.default} alt="logo" className="h-12" />
      </Link>
    </div>
  );

  const MobileMenu = () => (
    <div className="ml-auto flex lg:hidden">
      <button
        type="button"
        className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:bg-gray-700 focus:text-white"
        onClick={() => setMenuOpen(!menuIsOpen)}
      >
        <svg
          className={classnames({ hidden: menuIsOpen }, "h-6 w-6")}
          stroke="currentColor"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
        <svg
          className={classnames({ hidden: !menuIsOpen }, "h-6 w-6")}
          stroke="currentColor"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );

  const MenuItems = () => {
    const menuItem = (menu, path) => {
      const menuPath = `/${path || menu.toLowerCase()}`;
      const isCurrentPath = pathname === menuPath;

      return (
        <Link
          to={menuPath}
          className={classnames({
            "mr-4 text-offwhite px-3 py-2": !menuIsOpen,
            "p-4 border-b border-gray-100 text-white": menuIsOpen,
            "border-b-2 text-easter font-semibold border-easter": isCurrentPath,
          })}
        >
          {menu}
        </Link>
      );
    };

    const pressItem = () => {
      const isCurrentPath = false;
      return (
        <a
          href="https://hello.giglifepro.com/press/"
          className={classnames({
            "mr-4 text-offwhite px-3 py-2": !menuIsOpen,
            "p-4 border-b border-gray-100 text-white": menuIsOpen,
            "border-b-2 text-easter font-semibold border-easter": isCurrentPath,
          })}
        >
          Press (new!)
        </a>
      );
    };

    const partnershipsItem = () => {
      const isCurrentPath = false;
      return (
        <a
          href="https://hello.giglifepro.com/partnerships/"
          className={classnames({
            "mr-4 text-offwhite px-3 py-2": !menuIsOpen,
            "p-4 border-b border-gray-100 text-white": menuIsOpen,
            "border-b-2 text-easter font-semibold border-easter": isCurrentPath,
          })}
        >
          Partnerships
        </a>
      );
    };

    return (
      <div className="flex flex-col lg:flex-row sm:flex-grow sm:justify-around xl:mr-16 text-sm">
        {menuItem("About Us", "about")}
        {menuItem("Membership")}
        {partnershipsItem()}
        {menuItem("Events")}
        {menuItem("Locations")}
        {menuItem("Articles")}
        {menuItem("Consultation")}
        {pressItem()}
      </div>
    );
  };

  const ActionButtons = () => {

    const isPauseSignup = useCheckSignupPause();
    const params = new URLSearchParams(search);
    const memberTier = params.get('memberTier');
    if (isSubscribed)
      return (
        <Button
          label="Sign Out"
          outline
          transparent
          onClick={() => dispatch(Actions.signOut())}
          className="ml-auto self-center text-sm"
        />
      );

    if (isTablet) {
      return (
        <div className="flex flex-col content-center items-center gap-1 -mt-8">
          <style>{`
            .override-py-1 {
              padding-top: 0.25rem !important;
              padding-bottom: 0.25rem !important;
            }
          `}</style>
          <Button
            outline
            transparent
            label="Login"
            onClick={() => navigate("/login")}
            className="text-xxxs w-30 override-py-1"
            isPill
          />
          {!isPauseSignup && <Button
            label="SUBMIT YOUR APPLICATION"
            onClick={() => {
              if (memberTier) {
                window.location.href = `/signup`;
              } else {
                navigate('/signup');
              }
            }}
            className="text-xxxs override-py-1 w-32"
            isPill
          />}
        </div>
      )
    }

    return (
      <>
        <Button
          outline
          transparent
          label="Login"
          onClick={() => navigate("/login")}
          className="mr-4 px-10 md:px-4 lg:px-10 text-sm"
          isPill
        />
        {!isPauseSignup && <Button
          label="SUBMIT YOUR APPLICATION"
          onClick={() => {
            if (memberTier) {
              window.location.href = `/signup`;
            } else {
              navigate('/signup');
            }
          }}
          className="mr-4 xl:mr-0 px-10 md:px-4 lg:px-10 text-sm"
          isPill
        />}
      </>
    );
  };

  return (
    <div className="sticky top-0 z-50 w-full flex flex-col">
      <PlatformMaintenanceBanner />
      <div className="relative w-full">
        <nav
          className={classnames("absolute w-full transition-all duration-500 z-30", {
            "bg-ebony": scrollPosition > 60 || menuIsOpen,
          })}
        >
      {!isProduction && <StagingBanner />}
      <div className="container mx-auto px-4 sm:px-6 lg:px-0 xl:px-0">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center sm:flex-grow">
            <Logo />
            <div className="hidden lg:flex items-center justify-between w-full">
              <div className="ml-10 flex items-baseline sm:flex-grow">
                <MenuItems />
              </div>
            </div>
          </div>
          <div className="hidden lg:flex lg:flex-col xl:flex-row lg:justify-between lg:space-y-2 xl:space-y-0 lg:pt-10 xl:pt-0">
            <ActionButtons />
          </div>
          <MobileMenu />
        </div>
      </div>
      <div className={classnames({ hidden: !menuIsOpen }, "lg:hidden")}>
        <div className="flex flex-col mx-4">
          <MenuItems />
          <div className="flex justify-evenly my-4">
            <ActionButtons />
          </div>
        </div>
      </div>
    </nav>
      </div>
    </div>
  );
};

export default Navbar;
